import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { BadgeCheckIcon, MapPinIcon, XIcon } from 'lucide-react';
import { buyerNames, buyerCities, purchasedOffers, purchaseTimeAgo } from '../data/socialProof';
type Purchase = {
  key: number;
  name: string;
  city: string;
  offer: string;
  timeAgo: string;
};
const FIRST_DELAY = 9000;
const VISIBLE_MS = 6500;
const MIN_GAP_MS = 40000;
const MAX_GAP_MS = 60000;
function pick<T>(items: T[], avoid?: T): T {
  const pool = avoid && items.length > 1 ? items.filter(item => item !== avoid) : items;
  return pool[Math.floor(Math.random() * pool.length)];
}
function initials(name: string): string {
  return name.split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase();
}
type PurchaseToastProps = {
  liftedForStickyBar?: boolean;
};
export function PurchaseToast({
  liftedForStickyBar = true
}: PurchaseToastProps) {
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const lastNameRef = useRef<string | undefined>(undefined);
  const timersRef = useRef<number[]>([]);
  const reduceMotion = useReducedMotion();
  const clearTimers = useCallback(() => {
    timersRef.current.forEach(id => window.clearTimeout(id));
    timersRef.current = [];
  }, []);
  useEffect(() => {
    if (dismissed) {
      clearTimers();
      setPurchase(null);
      return;
    }
    const schedule = (delay: number) => {
      const showId = window.setTimeout(() => {
        const name = pick(buyerNames, lastNameRef.current);
        lastNameRef.current = name;
        setPurchase({
          key: Date.now(),
          name,
          city: pick(buyerCities),
          offer: pick(purchasedOffers),
          timeAgo: pick(purchaseTimeAgo)
        });
        const hideId = window.setTimeout(() => {
          setPurchase(null);
          schedule(MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS));
        }, VISIBLE_MS);
        timersRef.current.push(hideId);
      }, delay);
      timersRef.current.push(showId);
    };
    schedule(FIRST_DELAY);
    return clearTimers;
  }, [dismissed, clearTimers]);
  return <div aria-live="polite" aria-atomic="true" className={`pointer-events-none fixed left-3 z-30 w-[276px] sm:left-6 sm:w-[320px] ${liftedForStickyBar ? 'bottom-[84px] sm:bottom-6' : 'bottom-4 sm:bottom-6'}`} data-id="element-263" data-mp="28ttelc0j6sm6">
      <AnimatePresence data-id="element-264" data-mp="1c4c14avb429x">
        {purchase && <motion.div key={purchase.key} initial={{
        opacity: 0,
        y: reduceMotion ? 0 : 14,
        scale: reduceMotion ? 1 : 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: reduceMotion ? 0 : 14,
        scale: reduceMotion ? 1 : 0.97
      }} transition={{
        duration: reduceMotion ? 0 : 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto relative flex gap-3 rounded-xl border border-cream-300 bg-cream-50 p-3 pr-8 shadow-[0_16px_36px_-20px_rgba(14,31,22,0.55)]" data-id="element-265" data-mp="1c5n4waor5a11">
            <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-forest-800 font-display text-xs font-bold text-cream-50" data-id="element-266" data-mp="39318qog782a3">
              {initials(purchase.name)}
            </span>

            <div className="min-w-0" data-id="element-267" data-mp="3nrfgndsh2gxi">
              <p className="flex items-center gap-1 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-moss-600" data-id="element-268" data-mp="3nsgvltclznki">
                <BadgeCheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-269" data-mp="3sppc7pi3zjjn" />
                Compra verificada
              </p>
              <p className="mt-0.5 truncate text-[13px] font-semibold text-forest-900" data-id="element-270" data-mp="3j0pcu4zp2m4x">
                {purchase.name}
                <span className="font-normal text-forest-800/70" data-id="element-271" data-mp="akdumdzxm48k"> compró</span>
              </p>
              <p className="truncate text-[13px] text-forest-900" data-id="element-272" data-mp="fc47orq9763o">{purchase.offer}</p>
              <p className="mt-1 flex items-center gap-1 text-[11px] text-forest-800/70" data-id="element-273" data-mp="2muxeuvoweqzi">
                <MapPinIcon className="h-3 w-3 shrink-0 text-moss-600" aria-hidden="true" data-id="element-274" data-mp="2itlmiien3rnm" />
                <span className="truncate" data-id="element-275" data-mp="2ivybhfitf2a6">{purchase.city}</span>
                <span aria-hidden="true" data-id="element-276" data-mp="2dv0uv7dgegas">·</span>
                <span className="shrink-0" data-id="element-277" data-mp="28vnwpqh4tmsw">{purchase.timeAgo}</span>
              </p>
            </div>

            <button type="button" onClick={() => setDismissed(true)} aria-label="Ocultar avisos de compras" className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full text-forest-800/50 transition-colors duration-150 ease-out hover:bg-cream-200 hover:text-forest-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-800" data-id="element-278" data-mp="36iwpm6jgpy4x">
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-279" data-mp="3kixqhlzq95ke" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}